import React, { Children, Fragment, useCallback, useEffect, useRef, useState, useMemo, useContext, createContext } from 'react'
import cx from 'classnames'
import { animate, motion, useMotionValue } from 'framer-motion'
import { EmbedContext } from '../core/context'
import { STORYBOARD_TYPES } from '../../../constants/storyboardTypes'
import { Arrow, FullScreenView, SearchPopup, CarouselContext, CarouselNavigationContext, Page, BackgroundManager, KeyManager, Menu } from '.'
import DownloadButton from './DownloadButton'
import { useDesktop } from '../spatialContent'
import { measurements, LEFT_OFFSET, TOP_OFFSET, transition } from './constants'
import { useMediaQuery } from 'react-responsive'
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useLazyGesture } from './useLazyGesture'
import styles from './interactive.module.scss'

let initialHeightSet = false;

export function InteractiveContentV2({
  children,
  initialPage,
  appearance,
  downloadUrl,
  onChange,
  onDownload,
  versionTwo,
  gatingRef,
  anchorsRef,
}) {

  const [isEmbedded, setIsEmbedded] = useState(false)
  useEffect(() => {
    const handleIcWindowHeight = (event) => {
      if (event.data && event.data.type === 'cleverstory:embed:windowHeightData') {
        setIsEmbedded(true)
        setTimeout(() => initialHeightSet = true)
      }
    };
    window.addEventListener('message', handleIcWindowHeight);

    const requestWindowHeight = () => {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'cleverstory:embed:requestWindowHeight',
          source: 'cleverstory-ic-content'
        }, '*');
      }
    };
    requestWindowHeight();
    return () => {
      window.removeEventListener('message', handleIcWindowHeight);
    };
  }, []);


  return (
    <Carousel
      initialPage={initialPage}
      downloadUrl={downloadUrl}
      appearance={appearance}
      onChange={onChange}
      onDownload={onDownload}
      isEmbedded={isEmbedded}
      versionTwo={versionTwo}
      gatingRef={gatingRef}
      anchorsRef={anchorsRef}
    >
      {children}
    </Carousel>
  ) 
}

function Carousel({
  children,
  initialPage = 0,
  appearance,
  downloadUrl,
  onChange,
  onDownload,
  isEmbedded,
  versionTwo,
  gatingRef = {},
  anchorsRef = {}
}) {
  const containerRef = useRef(null)
  const wheelLockRef = useRef(false)
  const childrenArray = useMemo(() => Children.toArray(children), [children])
  const pages = Children.count(childrenArray)
  const singlePage = pages === 1
  const desktop = useDesktop()
  const x = useMotionValue(0)
  const [y, setY] = useState(singlePage ? TOP_OFFSET : 0)
  const [fullScreen, setFullScreen] = useState(singlePage)
  const [scrollPage, setScrollPage] = useState({
    progress: 0,
    scrollTop: 0
  })
  const [initialised, setInitialised] = useState(false)
  const [activeNavigation, setActiveNavigation] = useState('')
  const [activePage, setActivePage] = React.useState({
    index: initialPage,
    fullscreen: singlePage
  })
  const activePageRef = useRef(activePage);
  const [pagesMetadata, setPagesMetadata] = useState([])
  const [scale, setScale] = useState(0.9)
  const embedContext = useContext(EmbedContext);
  const actionScrolling = useRef(false);
  const desktopRef = useRef(desktop)
  
  const calculateNewX = (type) => {
    if (!desktop) return 0
    
    const left = measurements.leftOffset(desktop, y)
    const adjustments = LEFT_OFFSET - left
    const contianerWidth = containerRef.current?.clientWidth - 105
    const leftOffset = -activePage.index * contianerWidth - adjustments
    return leftOffset
  }

  const [triggeredGating, setTriggeredGating] = useState(null)
  
  const [carouselState, setCarouselState] = useState({
    initialised: initialised,
    activePage: activePage,
    pages: pages,
    triggeredGating: triggeredGating,
    setTriggeredGating: setTriggeredGating,
    navigateToPage: () => { }
  });

  const [carouselNavigationState, setCarouselNavigationState] = useState({
    scrollToTop: () => { },
    scrollToBottom: () => { },
    scrollToWidget: () => { },
    goToAnchor: () => {}
  })

  useEffect(() => {
    desktopRef.current = desktop
  }, [desktop])

  useEffect(() => {
    activePageRef.current = activePage;
  }, [activePage]);

  useEffect(() => {
    setCarouselState((state) => {
      return {
        ...state,
        initialised: initialised,
        activePage: activePage,
        pages: pages,
        triggeredGating: triggeredGating,
        setTriggeredGating: setTriggeredGating
      }
    })
  }, [initialised, activePage, pages, triggeredGating])

  useEffect(() => {
    if(initialised && embedContext.embedded) {
      const isInIframe = window.parent !== window;
      if (isInIframe) {
        const parentWindow = window.parent;
        parentWindow.postMessage({
          type: "cleverstory:embed:page:loading",
          payload: {
            id: activePage.index
          }
        }, '*');
      }
    }
  }, [initialised]);

  useEffect(() => {

    const offset = desktop ? 120 : 40

    const scale = (window.innerWidth - offset) / window.innerWidth
    setScale(scale)

    document.addEventListener('fullscreenchange', exitHandler)
    document.addEventListener('webkitfullscreenchange', exitHandler)
    document.addEventListener('mozfullscreenchange', exitHandler)
    document.addEventListener('MSFullscreenChange', exitHandler)

    const carouselEl = document.querySelector(`.${styles.carousel}`)
    const handleTouchMove = (e) => { e.preventDefault() }
    carouselEl?.addEventListener('touchmove', handleTouchMove, { passive: false })

    const timer = setTimeout(() => { setInitialised(true) }, 1000)
    return () => {
      document.removeEventListener('fullscreenchange', exitHandler)
      document.removeEventListener('webkitfullscreenchange', exitHandler)
      document.removeEventListener('mozfullscreenchange', exitHandler)
      document.removeEventListener('MSFullscreenChange', exitHandler)
      carouselEl?.removeEventListener('touchmove', handleTouchMove)
      clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    setCarouselNavigationState((state) => {
      return {
        ...state,
        scrollToTop: handleScrollTop,
        scrollToBottom: handleScrollBottom,
        scrollToWidget: handleScrollToWidget,
        goToAnchor: handleAnchorScroll,
      }
    });

    setCarouselState((state) => {
      return {
        ...state,
        navigateToPage: navigateToPage,
      }
    });
    const pagesMetadata = childrenArray.map((child) => {

      const name = get(child, 'props.name')
      const description = get(
        child,
        'props.icon.description'
      )
      const icon = get(child, 'props.icon.url')

      return {
        name: name,
        description: description,
        icon: icon
      }
    })

    setPagesMetadata(pagesMetadata)
  }, [childrenArray])

  useEffect(() => {
    const controls = animate(x, calculateNewX(), transition)

    triggerSectionGating()

    return () => {
      controls.stop()
    }
  }, [activePage.index])

  useEffect(() => {
    const { gatingContext = {} } = gatingRef.current ?? {};
    const { configurations } = gatingContext;

    if (configurations?.length) {
      triggerSectionGating();
    }
  }, [gatingRef.current?.gatingContext]);


  useEffect(() => {
    handleWidowResize();
  }, [desktop]);

  useEffect(() => {
    const controls = animate(x, calculateNewX(), {
      duration: 0
    })
     window.addEventListener('resize', handleWidowResize)
    calculateWindowHeight()
    return () => {
      window.removeEventListener('resize', handleWidowResize)
      controls.stop()
    }
  }, [desktop])

  useEffect(() => {
    if (!activePage.fullscreen && y > LEFT_OFFSET / 2) {
      setActivePage((state) => {
        return {
          ...state,
          fullscreen: true
        }
      })
    } else if (activePage.fullscreen && y < LEFT_OFFSET / 2) {
      setActivePage((state) => {
        return {
          ...state,
          fullscreen: false
        }
      })
    }

    const controls = animate(x, calculateNewX(), transition)
    return controls.stop
  }, [y])

  useEffect(() => {
    const newY = activePage.fullscreen ? TOP_OFFSET : 0
    setY(newY)
  }, [activePage.fullscreen])

  const triggerSectionGating = () => {
    const { gatingContext, triggerGating } = gatingRef.current || {};
    if (gatingContext?.preview) return;

    const sectionConfig = gatingContext?.configurations?.find((item) => {
      const section = get(item, 'trigger.value', 0);
      return get(item, 'trigger.type') === 'INTERACTIVE_SECTION' &&
            section === activePage.index + 1 &&
            get(item, 'gating.id');
    });

    if (sectionConfig) {
      triggerGating?.('INTERACTIVE_SECTION', null, sectionConfig);
    }
  };

  const handleEndDrag = (e, dragProps) => {
    if (!dragProps) {
      return
    }

    const clientWidth = containerRef.current?.clientWidth - 100 || 0

    const { offset, velocity } = dragProps

    if (Math.abs(velocity.y) > Math.abs(velocity.x)) {
      animate(x, calculateNewX(), transition)
      return
    }

    if (offset.x > clientWidth / 4) {
      navigateToPage(Math.max(0, activePage.index - 1), false)
    } else if (offset.x < -clientWidth / 4) {
      navigateToPage(Math.min(pages - 1, activePage.index + 1), false)
    } else {
      animate(x, calculateNewX(), transition)
    }
  }

  const handleGuesture = (offset) => {
    if (!desktop || singlePage) {
      return
    }

    if (activePage.fullscreen) {
      const activePageRef = containerRef.current.querySelector(
        `.${styles.page}.${styles.fullscreen}`
      )
      if (activePageRef) {
        const scrollTop = activePageRef.scrollTop
        if (scrollTop > 0) {
          return
        }
      }
    }

    setY((y) => {
      let newY = y + offset
      newY = Math.min(newY, TOP_OFFSET)
      newY = Math.max(newY, 0)

      return newY
    })
  }

  const navigateToPage = (index, fullscreen) => {
    const previousIndex = activePageRef.current.index;
    const { gatingContext } = gatingRef.current || {}
    if (index < 0 || index >= pages || (!isEmpty(gatingContext?.configuration) && !gatingContext?.disabled)) return; // prevents the navigation when the gating is in open state (allow when disabled = all clusters completed)

    if (previousIndex !== index) {
      const pageElement = containerRef.current.querySelector(`#page_${index}.interactive_content__page`);
      if (pageElement) {
        // Reset the scroll position for the page
        pageElement.scrollTop = 0;
      }
    }

    setActivePage((state) => {
      const isPageChange = state.index !== index;
      const newFullscreen = pages > 1
        ? (desktopRef.current
            ? fullscreen
            : (isPageChange ? (state.fullscreen || fullscreen) : fullscreen))
        : true;
      return { ...state, index, fullscreen: newFullscreen };
    });

    if (onChange && previousIndex !== index) {
      onChange('index', {
        index: index,
      });
    }

    var duration = 300;
    const diff = Math.abs(index - previousIndex);
    if (diff > 0) {
      duration = Math.abs(index - previousIndex) * 500;
    }

    return new Promise((resolve) => setTimeout(resolve, duration));
  }
  const handleWheel = useCallback((data) => {
    const { movement, event } = data;
    const isHorizontalWheel = Math.abs(event.deltaX) > Math.abs(event.deltaY);

    if (!isHorizontalWheel || Math.abs(event.deltaY) > 1) {
      handleGuesture(movement[1], 'vertical');
      return;
    }

    if (wheelLockRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const isTouchpad = Math.abs(event.deltaX) < 20;
    if (isTouchpad) {
      event.stopPropagation();
      return;
    }

    wheelLockRef.current = true;
    event.stopPropagation();
    event.preventDefault();

    if (event.deltaX > 0) {
      navigateToPage(activePage.index + 1, activePage.fullscreen);
    } else if (event.deltaX < 0) {
      navigateToPage(activePage.index - 1, activePage.fullscreen);
    }

    setTimeout(() => {
      wheelLockRef.current = false;
    }, 800);
  }, [activePage, handleGuesture, navigateToPage]);

  useLazyGesture(
    containerRef, 
    {
      onDrag: (data) => {
        const { movement } = data;
        handleGuesture(-movement[1], 'vertical');
      },
      onWheel: handleWheel
    }, 
    {
      eventOptions: { passive: false }
    }
  );  

  const handleWidowResize = () => {
    calculateWindowHeight()

    const offset = desktop || window.innerWidth >= 1024 ? 120 : 40
    const scale = (window.innerWidth - offset) / window.innerWidth
    setScale(scale)

    animate(x, calculateNewX(), {
      duration: 0
    })
  }

  const calculateWindowHeight = () => {
    if (initialHeightSet) {
      document.documentElement.style.setProperty(
        '--window-height',
        `${window.innerHeight}px`
      )
    }
  }

  const handleMenuChanage = (page) => {
    navigateToPage(page, false)
  }

  const handleNavigatePage = () => {
    const desktop = desktopRef.current;
    const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
    const scrollContainer = desktop
      ? containerRef.current.querySelector(selector)
      : document
        .getElementById(`page_${activePageRef.current.index}`)
        .querySelector(selector)

    if (scrollContainer.scrollTop === 0) {
      navigateToPage(activePageRef.current.index, singlePage)
      scrollContainer.removeEventListener('scroll', handleNavigatePage)
    }
  }

  const handleScrollTop = () => {
    const desktop = desktopRef.current;
    const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
    const scrollContainer = desktop
      ? containerRef.current.querySelector(selector)
      : document
        .getElementById(`page_${activePageRef.current.index}`)
        .querySelector(selector)
    scrollContainer.addEventListener('scroll', handleNavigatePage)
    scrollContainer.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  const handleScrollBottom = () => {
    const desktop = desktopRef.current;
    navigateToPage(activePageRef.current.index, true).then(() => {
      const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
      const scrollContainer = desktop
        ? containerRef.current.querySelector(selector)
        : document
          .getElementById(`page_${activePageRef.current.index}`)
          .querySelector(selector)

      const height = scrollContainer.scrollHeight
      scrollContainer.scroll({ top: height, left: 0, behavior: 'smooth' })
    })
  }

  const handleScrollDown = () => {
    navigateToPage(activePageRef.current.index, true).then(() => {
      const scrollContainer = containerRef.current.querySelector(
        `.${styles.fullscreen}`
      )
      if (!scrollContainer) return
      const height = scrollContainer.clientHeight
      scrollContainer.scroll({ top: height, left: 0, behavior: 'smooth' })
    })
  }

  const exitHandler = () => {
    if (
      !document.fullscreenElement &&
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement
    ) {
      setFullScreen(false)
    }
  }

  const handleMouseMove = (event) => {
    if (!initialHeightSet) { initialHeightSet = true }
    switch (true) {
      case event.clientY > (80 / 100) * window.innerHeight &&
        (event.clientX > (50 / 100) * window.innerWidth ||
          event.clientX < (50 / 100) * window.innerWidth):
        if (activeNavigation !== 'bottom') {
          setActiveNavigation('bottom')
        }
        break

      case event.clientX > (50 / 100) * window.innerWidth:
        if (activeNavigation !== 'right') {
          setActiveNavigation('right')
        }
        break

      case event.clientX < (50 / 100) * window.innerWidth:
        if (activeNavigation !== 'left') {
          setActiveNavigation('left')
        }
        break

      default:
        setActiveNavigation('')
        break
    }
  }

  const handleFullScreen = () => {
    const viewerContainer = document.body
    setFullScreen((prev) => !prev)

    const isFs =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement

    if (isFs) {
      if (document.exitFullscreen) document.exitFullscreen()
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen()
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen()
      else if (document.msExitFullscreen) document.msExitFullscreen()
      return
    }

    const req = viewerContainer.requestFullscreen || viewerContainer.webkitRequestFullscreen || viewerContainer.mozRequestFullScreen || viewerContainer.msRequestFullscreen
    if (req) req.call(viewerContainer)
  }

  const handleScrollToWidget = (widget) => {
    if (!widget) {
      return false
    }

    const { value, type } = widget

    const widgetElement = document.getElementById(value)
    if (!widgetElement) return;

    const toScrollItem = widgetElement.closest('.interactive_content__page')
    let pageId = toScrollItem.id
    pageId = pageId.split('_')
    let index = pageId[1]
    index = parseInt(index < 0 ? 0 : index)

    actionScrolling.current = true;

    navigateToPage(index, true).then(() => {
      setTimeout(() => {
        document.querySelector(`.${styles.carousel}`).scrollTop = 0;
        actionScrolling.current = false;
      }, 100);

      const rect = widgetElement.getBoundingClientRect();

      const selector = desktop ? `.${styles.fullscreen}` : `.${styles.body}`
      const scrollContainer = desktop
        ? containerRef.current.querySelector(selector)
        : document.querySelector(selector);
  
      const distanceToTop = rect.top + scrollContainer.scrollTop;

      if (scrollContainer) {
        if (type === 'carousel') {
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (!entry.isIntersecting) {

                if (!desktop) {
                  widgetElement.scrollIntoView({ behavior: 'smooth' });
                  return;
                }

                const screenPosition = 200; // screen position from top
                
                scrollContainer.scroll({
                  top: distanceToTop - screenPosition <= 0 ? 0 : distanceToTop - screenPosition,
                  left: 0,
                  behavior: 'smooth'
                })
              }

              observer.disconnect() // stop after first check
            },
            {
              threshold: 0 // partially visible check
            }
          )

          observer.observe(widgetElement)

          return;
        }

        if (!desktop) {
          widgetElement.scrollIntoView({ behavior: 'smooth' });
          return;
        }

        scrollContainer.scroll({
          top: distanceToTop,
          left: 0,
          behavior: 'smooth'
        })
      }
    })
  }

  const handleAnchorScroll = ({ pageId, widget }) => {
    const index = children.findIndex(item => item.key === pageId);
    if (index === -1) return;

    // 1. SAME PAGE CHECK
    if (index === activePage.index) {
      handleScrollToWidget({ value: widget });
      return;
    }

    const data = { pageId, widget };

    anchorsRef.current = {
      anchorScrollData: data,
      handleScrollToWidget
    };

    navigateToPage(index, true);
  }

  const dynamicGatingContext = useMemo(
    () => ({
      get configurations() {
        return gatingRef.current?.gatingContext?.configurations || []
      },
      get preview() {
        return gatingRef.current?.gatingContext?.preview
      },
      triggerGating: (...args) => gatingRef.current?.triggerGating(...args)
    }),
    []
  )
  
  useEffect(() => {
      if (!desktop && activePage){
        const pageElement = document.getElementById(`page_${activePage.index}`);
        if (pageElement){
          pageElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
  },[desktop])

  return (
    <CarouselContext.Provider value={carouselState}>
      <CarouselNavigationContext.Provider value={carouselNavigationState}>
        {!initialised && (
          <div className={styles.loading}>
            <div className={styles.loader}>
              <video loop autoPlay playsInline muted>
                <source src='/images/shortLoader.mp4' type='video/mp4' />
              </video>
              <label>Loading Interactive content...</label>
            </div>
          </div>
        )}
        <motion.div
          ref={containerRef}
          style={{ '--window-height': isEmbedded ? '815px' : '100vh' }}
          className={cx(styles.carousel, {
            [styles.singlePage]: singlePage,
          })}
        >
          {appearance && (
            <Fragment>
              <div
                className={cx(
                  styles.overlay,
                  {[styles.overlayFullScreen]: activePage.fullscreen},
                  { [styles.lightOverlay]: appearance.overlay === 'light' },
                  { [styles.darkOverlay]: appearance.overlay === 'dark' }
                )}
              />
              <BackgroundManager background={appearance.background} />
            </Fragment>
          )}

          <KeyManager>
            {!singlePage && (
              <Arrow
                direction='left'
                offset={scrollPage.scrollTop}
                activePage={activePage}
                show={
                  activeNavigation == 'left' &&
                  scrollPage.scrollTop <= window.innerHeight
                }
                disabled={activePage.index <= 0 || activeNavigation !== 'left'}
                onClick={() => navigateToPage(activePage.index - 1, false)}
              />
            )}

            {!singlePage && (
              <Menu
                pages={pagesMetadata}
                offset={y}
                color={get(appearance, 'iconColor', {})}
                active={activePage.index}
                onChange={handleMenuChanage}
              />
            )}

            <motion.div
              className={cx(styles.rightMenu, {
                [styles.activePageMenu]: activePage.fullscreen
              })}
              animate={{
                transform: `translate3D(0, -${y}px, 0)`
              }}
              transition={transition}
            >
              {(appearance.disableDownload === false) && downloadUrl && (
                <DownloadButton
                  iconColor={get(appearance, 'iconColor', {})}
                  onDownload={onDownload}
                  gatingContext={dynamicGatingContext}
                />
              )}
              <SearchPopup iconColor={get(appearance, 'iconColor', {})} />

              {desktop && (
                <FullScreenView
                  iconColor={get(appearance, 'iconColor', {})}
                  onFullScreen={handleFullScreen}
                  fullScreen={fullScreen}
                />
              )}
            </motion.div>

            <motion.div
              className={cx(styles.track)}
              style={{
                x
              }}
              initial={false}
              draggable
              drag={false}
              dragElastic={1}
              onDragEnd={handleEndDrag}
            >
              {childrenArray.map((child, index) => {
                const fullscreen = activePage.index === index ? activePage.fullscreen : false
                return (
                  <Page
                    key={child.key}
                    index={index}
                    active={activePage.index === index}
                    offset={activePage.index === index ? y : 0}
                    scale={scale}
                    fullscreen={fullscreen}
                    actionScrolling={actionScrolling}
                    handleMouseMove={handleMouseMove}
                    onDragEnd={handleEndDrag}
                    toggleFullScreen={(index, fullscreen) => {
                      navigateToPage(index, fullscreen)
                    }}
                    onScroll={(progress, scrollTop) => {
                      setScrollPage({ progress, scrollTop });
                      onChange?.('progress', { index, progress });
                    }}
                    isEmbedded={isEmbedded}
                    versionTwo={versionTwo}
                    singlePage={singlePage}
                  >
                    {child} {/* Use React.cloneElement if need to inject props to the child in future */}
                  </Page>
                )
              })}
            </motion.div>
            <Arrow
              direction='bottom'
              offset={y}
              activePage={activePage}
              show={activeNavigation === 'bottom' && !activePage.fullscreen}
              disabled={scrollPage.progress === 100}
              onClick={handleScrollDown}
            />

            {!singlePage && (
              <Arrow
                direction='right'
                offset={scrollPage.scrollTop}
                activePage={activePage}
                show={
                  activeNavigation === 'right' &&
                  scrollPage.scrollTop <= window.innerHeight
                }
                disabled={
                  activePage.index >= childrenArray.length - 1 ||
                  activeNavigation !== 'right'
                }
                onClick={() =>  navigateToPage(activePage.index + 1, false)}
              />
            )}
          </KeyManager>
        </motion.div>
      </CarouselNavigationContext.Provider>
    </CarouselContext.Provider>
  )
}

// Global Provider for InteractiveContent Version Two
const InteractiveContentContext = createContext({});

export const InteractiveContentProvider = ({ children, value }) => (
  <InteractiveContentContext.Provider value={value}>
    {children}
  </InteractiveContentContext.Provider>
);

export const useInteractiveContent = () => useContext(InteractiveContentContext);

export const isIcV2 = (type) => type === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2;
export const resetMarginOnTablet = (isTablet = false) => (isTablet ? { marginLeft: 0 } : {});

export const useInteractiveContentBreakpoints = () => {
  const tabletScreens = useMediaQuery({ minWidth: 1024, maxWidth: 1320 })
  const extraSmallScreens = useMediaQuery({ maxWidth: 420 });
  return { tabletScreens, extraSmallScreens };
}